import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SafeMarkdown } from "../components/SafeMarkdown";
import { FinanceAdvisor } from "../components/FinanceAdvisor";
import { toast } from "sonner";
import { 
  DollarSign, 
  Clock, 
  TrendingUp, 
  Users, 
  FolderKanban,
  AlertCircle,
  CheckCircle,
  FileText,
  Brain,
  Sparkles,
  Calculator,
  TrendingDown,
  Send,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from "recharts";

const CHART_COLORS = [
  "#2E5C46",
  "#4A8F76",
  "#78B89A",
  "#FFB347",
  "#FF6B6B",
  "#A6D4C3"
];

export default function DashboardPage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [revenueByClient, setRevenueByClient] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [yearComparison, setYearComparison] = useState([]);
  const [earningsAvg, setEarningsAvg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  
  // AI Agents state
  const [selectedAgent, setSelectedAgent] = useState("finance");
  const [aiQuery, setAiQuery] = useState("");
  const [aiResponse, setAiResponse] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  
  // View controls
  const [viewType, setViewType] = useState("calendar");
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedClient, setSelectedClient] = useState("all");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Fetch clients for the filter dropdown
  useEffect(() => {
    axios.get(`${API}/clients`, { headers })
      .then(res => setClients(res.data || []))
      .catch(() => {});
  }, [headers]);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view_type: viewType };
      params.year = selectedYear; // pass through ('all' supported by backend)
      if (selectedClient !== "all") params.client_name = selectedClient;

      const [summaryRes, clientRes, yearRes, earningsRes] = await Promise.all([
        axios.get(`${API}/analytics/financial-overview`, { headers, params }),
        axios.get(`${API}/analytics/client-profitability`, { headers, params }),
        axios.get(`${API}/analytics/year-comparison`, { headers, params: { view_type: viewType, ...(selectedClient !== "all" && { client_name: selectedClient }) } }),
        axios.get(`${API}/analytics/earnings-averages`, { headers })
      ]);

      setSummary(summaryRes.data.summary);
      setMonthlyRevenue(summaryRes.data.by_month || []);
      setRevenueByClient(clientRes.data.clients || []);
      setYearComparison(yearRes.data.years || []);
      setEarningsAvg(earningsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [headers, viewType, selectedYear, selectedClient]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const askAIAgent = async () => {
    if (!aiQuery.trim()) {
      toast.error("Please enter a question");
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      const endpoint = selectedAgent === "finance" 
        ? "/ai/finance-agent" 
        : selectedAgent === "accountant" 
          ? "/ai/accountant-agent" 
          : "/ai/trend-agent";
      
      const response = await axios.post(`${API}${endpoint}`, 
        { query: aiQuery },
        { headers }
      );
      
      setAiResponse(response.data);
    } catch (error) {
      toast.error("AI Agent error: " + (error.response?.data?.detail || "Unknown error"));
    } finally {
      setAiLoading(false);
    }
  };

  const getQuickInsights = async (agent) => {
    setSelectedAgent(agent);
    setAiLoading(true);
    setAiResponse(null);
    
    const queries = {
      finance: "Give me a quick overview of my financial health with top 3 recommendations",
      accountant: "Which invoices should I chase immediately? Give me a prioritized action list.",
      trend: "What's my revenue forecast for the next 3 months?"
    };
    
    setAiQuery(queries[agent]);
    
    try {
      const endpoint = agent === "finance" 
        ? "/ai/finance-agent" 
        : agent === "accountant" 
          ? "/ai/accountant-agent" 
          : "/ai/trend-agent";
      
      const response = await axios.post(`${API}${endpoint}`, 
        { query: queries[agent] },
        { headers }
      );
      
      setAiResponse(response.data);
    } catch (error) {
      toast.error("AI Agent error");
    } finally {
      setAiLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const calendarYears = ["all", "2024", "2025", "2026"];
  const financialYears = ["all", "FY24", "FY25", "FY26", "FY27"];

  if (loading && !summary) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6" data-testid="dashboard-page">
        {/* View Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={viewType} onValueChange={setViewType}>
              <SelectTrigger className="w-[160px]" data-testid="dashboard-view-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="calendar">Calendar Year</SelectItem>
                <SelectItem value="financial">Financial Year</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]" data-testid="dashboard-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(viewType === "financial" ? financialYears : calendarYears).map((year) => (
                  <SelectItem key={year} value={year}>
                    {year === "all" ? "All Years" : year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-[180px]" data-testid="dashboard-client-filter">
                <Users className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id || c.name} value={c.name}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={fetchDashboardData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-hover" data-testid="revenue-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Revenue (inc GST)</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(summary?.total_invoiced)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ex-GST: <span className="font-semibold text-foreground">{formatCurrency(summary?.total_invoiced_ex_gst)}</span>
                    {" · "}GST: {formatCurrency(summary?.total_gst_collected)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {summary?.invoice_count || 0} invoices
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="collected-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Collected (inc GST)</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(summary?.total_paid)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ex-GST: <span className="font-semibold text-foreground">{formatCurrency(summary?.total_paid_ex_gst)}</span>
                  </p>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs mt-1">
                    {summary?.collection_rate || 0}% collected
                  </Badge>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Outstanding</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {formatCurrency(summary?.total_pending)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    DSO: {summary?.days_sales_outstanding || 0} days
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Invoice</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(summary?.average_invoice_value)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Per invoice
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Earnings Averages — working-day based */}
        {earningsAvg && (
          <div className="space-y-4" data-testid="earnings-averages-section">
            <div className="flex items-baseline justify-between">
              <h3 className="text-base font-semibold text-primary">Earnings Averages</h3>
              <p className="text-xs text-muted-foreground">
                Based on working days (Mon-Fri). Earnings = invoiced (paid + pending).
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="card-hover border-l-4" style={{ borderLeftColor: "#2E5C46" }}>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Avg Daily (All-time)</p>
                  <p className="text-2xl font-bold text-primary mt-1" data-testid="avg-daily-alltime">
                    {formatCurrency(earningsAvg.all_time?.daily_avg)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {earningsAvg.all_time?.working_days || 0} working days
                    {earningsAvg.all_time?.start_date && (
                      <> · since {earningsAvg.all_time.start_date}</>
                    )}
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover border-l-4" style={{ borderLeftColor: "#4A8F76" }}>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Avg Monthly (All-time)</p>
                  <p className="text-2xl font-bold text-primary mt-1" data-testid="avg-monthly-alltime">
                    {formatCurrency(earningsAvg.all_time?.monthly_avg)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Across {earningsAvg.all_time?.months || 0} months
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover border-l-4" style={{ borderLeftColor: "#FFB347" }}>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Avg Daily (Last 90d)</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: "#C77E0F" }} data-testid="avg-daily-90d">
                    {formatCurrency(earningsAvg.last_90d?.daily_avg)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {earningsAvg.last_90d?.working_days || 0} working days
                  </p>
                </CardContent>
              </Card>

              <Card className="card-hover border-l-4" style={{ borderLeftColor: "#78B89A" }}>
                <CardContent className="pt-6">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Avg Monthly (Last 90d)</p>
                  <p className="text-2xl font-bold mt-1" style={{ color: "#2E5C46" }} data-testid="avg-monthly-90d">
                    {formatCurrency(earningsAvg.last_90d?.monthly_avg)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Earnings: {formatCurrency(earningsAvg.last_90d?.earnings)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Trend chart: rolling 3-month buckets */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">3-Month Rolling Averages</CardTitle>
                <CardDescription>
                  How your daily &amp; monthly earning rate has changed every 3 months (last 24 months)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {earningsAvg.trend?.length > 0 ? (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={earningsAvg.trend} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="period"
                          tick={{ fontSize: 11 }}
                          interval={0}
                          angle={-20}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
                        />
                        <Tooltip
                          formatter={(v, name) => [formatCurrency(v), name]}
                          labelStyle={{ color: "#2E5C46" }}
                        />
                        <Legend />
                        <Line
                          yAxisId="left"
                          type="monotone"
                          dataKey="monthly_avg"
                          name="Avg Monthly"
                          stroke="#2E5C46"
                          strokeWidth={2.5}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="daily_avg"
                          name="Avg Daily"
                          stroke="#FFB347"
                          strokeWidth={2.5}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-48 text-muted-foreground">
                    Not enough historical data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Year-over-Year Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Year-over-Year Revenue</CardTitle>
              <CardDescription>Revenue comparison across years</CardDescription>
            </CardHeader>
            <CardContent>
              {yearComparison.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={yearComparison}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="paid" fill="#2E5C46" name="Collected" />
                    <Bar dataKey="pending" fill="#FFB347" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No data available
                </div>
              )}
              {/* YoY Growth badges */}
              <div className="flex gap-2 mt-4 flex-wrap">
                {yearComparison.map((year) => (
                  year.yoy_growth !== 0 && (
                    <Badge 
                      key={year.year}
                      variant="secondary" 
                      className={year.yoy_growth > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
                    >
                      {year.year}: {year.yoy_growth > 0 ? '+' : ''}{year.yoy_growth}%
                    </Badge>
                  )
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Finance Advisor (embedded, compact — full view on /finance-advisor) */}
          <div className="pt-2">
            <FinanceAdvisor compact />
          </div>

          {/* Revenue by Client */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue by Client</CardTitle>
              <CardDescription>Top clients by revenue contribution</CardDescription>
            </CardHeader>
            <CardContent>
              {revenueByClient.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={revenueByClient.slice(0, 5)}
                      dataKey="revenue"
                      nameKey="client"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ client, revenue_share }) => `${client.substring(0, 10)} (${revenue_share}%)`}
                    >
                      {revenueByClient.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Revenue Trend</CardTitle>
            <CardDescription>Paid vs Pending by month</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyRevenue.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Area type="monotone" dataKey="paid" stackId="1" stroke="#2E5C46" fill="#2E5C46" name="Paid" />
                  <Area type="monotone" dataKey="pending" stackId="1" stroke="#FFB347" fill="#FFB347" name="Pending" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insights Panel */}
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              AI Financial Insights
            </CardTitle>
            <CardDescription>
              Ask our AI agents for financial advice, collection priorities, or revenue forecasts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => getQuickInsights("finance")}
                disabled={aiLoading}
                className="gap-2"
              >
                <Brain className="w-4 h-4" />
                Financial Health
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => getQuickInsights("accountant")}
                disabled={aiLoading}
                className="gap-2"
              >
                <Calculator className="w-4 h-4" />
                Chase Invoices
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => getQuickInsights("trend")}
                disabled={aiLoading}
                className="gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Revenue Forecast
              </Button>
            </div>

            {/* Custom Query */}
            <div className="flex gap-2">
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="finance">
                    <span className="flex items-center gap-2">
                      <Brain className="w-4 h-4" /> Finance Agent
                    </span>
                  </SelectItem>
                  <SelectItem value="accountant">
                    <span className="flex items-center gap-2">
                      <Calculator className="w-4 h-4" /> Accountant Agent
                    </span>
                  </SelectItem>
                  <SelectItem value="trend">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> Trend Analyzer
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Ask a question about your finances..."
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                className="min-h-[40px] max-h-[100px]"
                data-testid="ai-query-input"
              />
              <Button onClick={askAIAgent} disabled={aiLoading} data-testid="ask-ai-btn">
                {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>

            {/* AI Response */}
            {aiLoading && (
              <div className="p-4 bg-muted/50 rounded-lg animate-pulse">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  AI is analyzing your data...
                </div>
              </div>
            )}

            {aiResponse && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="outline" className="capitalize">
                    {aiResponse.agent} Agent
                  </Badge>
                  <span>•</span>
                  <span className="truncate">{aiResponse.query}</span>
                </div>
                <SafeMarkdown
                  className="prose prose-sm max-w-none dark:prose-invert prose-headings:text-lg prose-headings:font-semibold prose-p:my-2"
                  content={aiResponse.response}
                  testId="ai-response"
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
